import { useState, useEffect } from 'react';

// Enhanced language-based structure types
export interface LanguageConfig {
  code: 'ar' | 'en';
  name: string;
  nativeName: string;
  direction: 'rtl' | 'ltr';
  flag: string;
  enabled: boolean;
}

export interface ResponsiveValue<T> {
  desktop: T;
  tablet: T;
  mobile: T;
}

export interface SpacingValue {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface TypographySettings {
  fontFamily: string;
  fontSize: ResponsiveValue<number>;
  fontWeight: number;
  lineHeight: number;
  letterSpacing: number;
  textAlign: 'left' | 'center' | 'right' | 'justify';
  textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  color: string;
}

export interface ImageSettings {
  src: string;
  alt: string;
  width: ResponsiveValue<number>;
  height: ResponsiveValue<number>;
  objectFit: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  objectPosition: string;
  borderRadius: ResponsiveValue<number>;
  overlay: {
    enabled: boolean;
    color: string;
    opacity: number;
  };
}

export interface LayoutSettings {
  margin: ResponsiveValue<SpacingValue>;
  padding: ResponsiveValue<SpacingValue>;
  width: ResponsiveValue<string>;
  height: ResponsiveValue<string>;
  maxWidth: ResponsiveValue<string>;
  minHeight: ResponsiveValue<string>;
  display: ResponsiveValue<'block' | 'flex' | 'grid' | 'none'>;
  flexDirection: ResponsiveValue<'row' | 'column'>;
  justifyContent: ResponsiveValue<'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly'>;
  alignItems: ResponsiveValue<'flex-start' | 'center' | 'flex-end' | 'stretch'>;
  gap: ResponsiveValue<number>;
  backgroundColor: string;
  backgroundImage: string;
  borderRadius: ResponsiveValue<number>;
  border: {
    width: ResponsiveValue<number>;
    style: 'solid' | 'dashed' | 'dotted' | 'none';
    color: string;
  };
  boxShadow: string;
  zIndex: number;
}

export interface Widget {
  id: string;
  type: 'text' | 'heading' | 'image' | 'button' | 'spacer' | 'divider' | 'icon' | 'video' | 'form' | 'custom';
  name: string;
  content: any;
  typography?: TypographySettings;
  image?: ImageSettings;
  layout: LayoutSettings;
  animation?: {
    type: string;
    duration: number;
    delay: number;
  };
  conditions?: {
    device: ('desktop' | 'tablet' | 'mobile')[];
    userRole: string[];
  };
  locked: boolean;
  version: number;
}

export interface Column {
  id: string;
  width: ResponsiveValue<number>; // Percentage
  widgets: Widget[];
  layout: LayoutSettings;
  locked: boolean;
}

export interface Section {
  id: string;
  name: string;
  type: 'header' | 'hero' | 'content' | 'footer' | 'custom';
  enabled: boolean;
  columns: Column[];
  layout: LayoutSettings;
  order: number;
  locked: boolean;
  version: number;
}

export interface LanguagePage {
  id: string;
  slug: string;
  title: string;
  description: string;
  sections: Section[];
  seo: {
    metaTitle: string;
    metaDescription: string;
    keywords: string[];
    ogTitle: string;
    ogDescription: string;
    ogImage: string;
    canonicalUrl: string;
    robots: string;
    structuredData: any;
  };
  settings: {
    customCSS: string;
    customJS: string;
    bodyClass: string;
  };
  status: 'draft' | 'published' | 'scheduled';
  publishedAt?: string;
  scheduledAt?: string;
  version: number;
  lastModified: string;
}

export interface LanguageWebsite {
  pages: Record<string, LanguagePage>;
  globalComponents: {
    header: Section[];
    footer: Section[];
  };
  theme: {
    colors: {
      primary: string;
      secondary: string;
      accent: string;
      background: string;
      surface: string;
      text: string;
      textSecondary: string;
      border: string;
      success: string;
      warning: string;
      error: string;
    };
    typography: {
      fontFamilies: string[];
      headings: TypographySettings;
      body: TypographySettings;
      buttons: TypographySettings;
      navigation: TypographySettings;
    };
    spacing: {
      unit: number;
      scale: number[];
    };
    breakpoints: {
      mobile: number;
      tablet: number;
      desktop: number;
    };
  };
  assets: {
    images: Record<string, {
      id: string;
      url: string;
      alt: string;
      width: number;
      height: number;
      size: number;
      type: string;
      uploadedAt: string;
    }>;
    videos: Record<string, any>;
    documents: Record<string, any>;
  };
  settings: {
    siteName: string;
    tagline: string;
    logo: ImageSettings;
    favicon: string;
    googleAnalytics: string;
    googleTagManager: string;
    customCode: {
      head: string;
      bodyStart: string;
      bodyEnd: string;
    };
  };
}

export interface MultiLanguageWebsite {
  languages: Record<string, LanguageConfig>;
  defaultLanguage: 'ar' | 'en';
  websites: Record<'ar' | 'en', LanguageWebsite>;
  sharedAssets: {
    images: Record<string, any>;
    videos: Record<string, any>;
    documents: Record<string, any>;
  };
  globalSettings: {
    domain: string;
    timezone: string;
    dateFormat: string;
    currency: string;
    maintenance: {
      enabled: boolean;
      message: Record<'ar' | 'en', string>;
    };
  };
  version: string;
  lastModified: string;
}

// Default responsive values
export const createResponsiveValue = <T>(value: T): ResponsiveValue<T> => ({
  desktop: value,
  tablet: value,
  mobile: value
});

export const createSpacingValue = (value: number = 0): SpacingValue => ({
  top: value,
  right: value,
  bottom: value,
  left: value
});

export const createDefaultLayout = (): LayoutSettings => ({
  margin: createResponsiveValue(createSpacingValue(0)),
  padding: createResponsiveValue(createSpacingValue(16)),
  width: createResponsiveValue('100%'),
  height: createResponsiveValue('auto'),
  maxWidth: createResponsiveValue('none'),
  minHeight: createResponsiveValue('auto'),
  display: createResponsiveValue('block'),
  flexDirection: createResponsiveValue('column'),
  justifyContent: createResponsiveValue('flex-start'),
  alignItems: createResponsiveValue('stretch'),
  gap: createResponsiveValue(16),
  backgroundColor: 'transparent',
  backgroundImage: '',
  borderRadius: createResponsiveValue(0),
  border: {
    width: createResponsiveValue(0),
    style: 'none',
    color: 'transparent'
  },
  boxShadow: 'none',
  zIndex: 0
});

export const createDefaultTypography = (): TypographySettings => ({
  fontFamily: 'Cairo',
  fontSize: createResponsiveValue(16),
  fontWeight: 400,
  lineHeight: 1.6,
  letterSpacing: 0,
  textAlign: 'left',
  textTransform: 'none',
  color: '#000000'
});

// Default website structure
export const createDefaultMultiLanguageWebsite = (): MultiLanguageWebsite => ({
  languages: {
    ar: {
      code: 'ar',
      name: 'Arabic',
      nativeName: 'العربية',
      direction: 'rtl',
      flag: '🇸🇦',
      enabled: true
    },
    en: {
      code: 'en',
      name: 'English',
      nativeName: 'English',
      direction: 'ltr',
      flag: '🇺🇸',
      enabled: true
    }
  },
  defaultLanguage: 'ar',
  websites: {
    ar: {
      pages: {
        home: {
          id: 'home',
          slug: '/',
          title: 'الصفحة الرئيسية',
          description: 'الصفحة الرئيسية لوكالة ريشة للتسويق',
          sections: [
            {
              id: 'hero-ar',
              name: 'القسم الرئيسي',
              type: 'hero',
              enabled: true,
              columns: [
                {
                  id: 'hero-col-ar',
                  width: createResponsiveValue(100),
                  widgets: [
                    {
                      id: 'hero-heading-ar',
                      type: 'heading',
                      name: 'العنوان الرئيسي',
                      content: {
                        text: 'ننمو بالبيانات، ونُبدع بالنتائج',
                        tag: 'h1'
                      },
                      typography: {
                        ...createDefaultTypography(),
                        fontSize: createResponsiveValue(48),
                        fontWeight: 700,
                        textAlign: 'center',
                        color: '#ffffff'
                      },
                      layout: createDefaultLayout(),
                      locked: false,
                      version: 1
                    },
                    {
                      id: 'hero-subtitle-ar',
                      type: 'text',
                      name: 'العنوان الفرعي',
                      content: {
                        text: 'تسويق ذكي. نمو حقيقي.'
                      },
                      typography: {
                        ...createDefaultTypography(),
                        fontSize: createResponsiveValue(24),
                        textAlign: 'center',
                        color: '#ffffff'
                      },
                      layout: createDefaultLayout(),
                      locked: false,
                      version: 1
                    },
                    {
                      id: 'hero-cta-ar',
                      type: 'button',
                      name: 'زر الإجراء',
                      content: {
                        text: 'تحليل نمو مجاني',
                        url: '/contact',
                        target: '_self'
                      },
                      typography: {
                        ...createDefaultTypography(),
                        fontSize: createResponsiveValue(18),
                        fontWeight: 600,
                        textAlign: 'center',
                        color: '#000000'
                      },
                      layout: {
                        ...createDefaultLayout(),
                        backgroundColor: '#ffffff',
                        borderRadius: createResponsiveValue(8),
                        padding: createResponsiveValue({
                          top: 16,
                          right: 32,
                          bottom: 16,
                          left: 32
                        })
                      },
                      locked: false,
                      version: 1
                    }
                  ],
                  layout: createDefaultLayout(),
                  locked: false
                }
              ],
              layout: {
                ...createDefaultLayout(),
                backgroundColor: '#000000',
                minHeight: createResponsiveValue('100vh'),
                display: createResponsiveValue('flex'),
                justifyContent: createResponsiveValue('center'),
                alignItems: createResponsiveValue('center')
              },
              order: 1,
              locked: false,
              version: 1
            }
          ],
          seo: {
            metaTitle: 'ريشة | وكالة تسويق نمو وأداء',
            metaDescription: 'وكالة تسويق رقمي متخصصة في النمو والأداء',
            keywords: ['تسويق رقمي', 'إعلانات', 'نمو', 'أداء'],
            ogTitle: 'ريشة | وكالة تسويق نمو وأداء',
            ogDescription: 'وكالة تسويق رقمي متخصصة في النمو والأداء',
            ogImage: '/images/og-ar.jpg',
            canonicalUrl: 'https://reesha.agency/ar',
            robots: 'index, follow',
            structuredData: {}
          },
          settings: {
            customCSS: '',
            customJS: '',
            bodyClass: 'rtl arabic-page'
          },
          status: 'published',
          version: 1,
          lastModified: new Date().toISOString()
        }
      },
      globalComponents: {
        header: [],
        footer: []
      },
      theme: {
        colors: {
          primary: '#000000',
          secondary: '#ffffff',
          accent: '#f0f0f0',
          background: '#000000',
          surface: '#111111',
          text: '#ffffff',
          textSecondary: '#cccccc',
          border: '#333333',
          success: '#10b981',
          warning: '#f59e0b',
          error: '#ef4444'
        },
        typography: {
          fontFamilies: ['Cairo', 'Noto Sans Arabic', 'Tajawal', 'Amiri'],
          headings: {
            ...createDefaultTypography(),
            fontFamily: 'Cairo',
            fontWeight: 700
          },
          body: {
            ...createDefaultTypography(),
            fontFamily: 'Cairo'
          },
          buttons: {
            ...createDefaultTypography(),
            fontFamily: 'Cairo',
            fontWeight: 600
          },
          navigation: {
            ...createDefaultTypography(),
            fontFamily: 'Cairo',
            fontWeight: 500
          }
        },
        spacing: {
          unit: 4,
          scale: [0, 4, 8, 12, 16, 20, 24, 32, 40, 48, 64, 80, 96, 128]
        },
        breakpoints: {
          mobile: 768,
          tablet: 1024,
          desktop: 1280
        }
      },
      assets: {
        images: {},
        videos: {},
        documents: {}
      },
      settings: {
        siteName: 'ريشة | وكالة تسويق نمو وأداء',
        tagline: 'ننمو بالبيانات، ونُبدع بالنتائج',
        logo: {
          src: '/images/LOGO-A9.png',
          alt: 'ريشة',
          width: createResponsiveValue(120),
          height: createResponsiveValue(40),
          objectFit: 'contain',
          objectPosition: 'center',
          borderRadius: createResponsiveValue(0),
          overlay: {
            enabled: false,
            color: '#000000',
            opacity: 0
          }
        },
        favicon: '/images/LOGO-A9.png',
        googleAnalytics: '',
        googleTagManager: '',
        customCode: {
          head: '',
          bodyStart: '',
          bodyEnd: ''
        }
      }
    },
    en: {
      pages: {
        home: {
          id: 'home',
          slug: '/',
          title: 'Home',
          description: 'Home page for Reesha Marketing Agency',
          sections: [
            {
              id: 'hero-en',
              name: 'Hero Section',
              type: 'hero',
              enabled: true,
              columns: [
                {
                  id: 'hero-col-en',
                  width: createResponsiveValue(100),
                  widgets: [
                    {
                      id: 'hero-heading-en',
                      type: 'heading',
                      name: 'Main Heading',
                      content: {
                        text: 'We Grow with Data, Create with Results',
                        tag: 'h1'
                      },
                      typography: {
                        ...createDefaultTypography(),
                        fontFamily: 'Montserrat',
                        fontSize: createResponsiveValue(48),
                        fontWeight: 700,
                        textAlign: 'center',
                        color: '#ffffff'
                      },
                      layout: createDefaultLayout(),
                      locked: false,
                      version: 1
                    },
                    {
                      id: 'hero-subtitle-en',
                      type: 'text',
                      name: 'Subtitle',
                      content: {
                        text: 'Smart Marketing. Real Growth.'
                      },
                      typography: {
                        ...createDefaultTypography(),
                        fontFamily: 'Roboto',
                        fontSize: createResponsiveValue(24),
                        textAlign: 'center',
                        color: '#ffffff'
                      },
                      layout: createDefaultLayout(),
                      locked: false,
                      version: 1
                    },
                    {
                      id: 'hero-cta-en',
                      type: 'button',
                      name: 'CTA Button',
                      content: {
                        text: 'Free Growth Analysis',
                        url: '/contact',
                        target: '_self'
                      },
                      typography: {
                        ...createDefaultTypography(),
                        fontFamily: 'Roboto',
                        fontSize: createResponsiveValue(18),
                        fontWeight: 600,
                        textAlign: 'center',
                        color: '#000000'
                      },
                      layout: {
                        ...createDefaultLayout(),
                        backgroundColor: '#ffffff',
                        borderRadius: createResponsiveValue(8),
                        padding: createResponsiveValue({
                          top: 16,
                          right: 32,
                          bottom: 16,
                          left: 32
                        })
                      },
                      locked: false,
                      version: 1
                    }
                  ],
                  layout: createDefaultLayout(),
                  locked: false
                }
              ],
              layout: {
                ...createDefaultLayout(),
                backgroundColor: '#000000',
                minHeight: createResponsiveValue('100vh'),
                display: createResponsiveValue('flex'),
                justifyContent: createResponsiveValue('center'),
                alignItems: createResponsiveValue('center')
              },
              order: 1,
              locked: false,
              version: 1
            }
          ],
          seo: {
            metaTitle: 'Reesha | Growth & Performance Marketing Agency',
            metaDescription: 'Digital marketing agency specialized in growth and performance',
            keywords: ['digital marketing', 'advertising', 'growth', 'performance'],
            ogTitle: 'Reesha | Growth & Performance Marketing Agency',
            ogDescription: 'Digital marketing agency specialized in growth and performance',
            ogImage: '/images/og-en.jpg',
            canonicalUrl: 'https://reesha.agency/en',
            robots: 'index, follow',
            structuredData: {}
          },
          settings: {
            customCSS: '',
            customJS: '',
            bodyClass: 'ltr english-page'
          },
          status: 'published',
          version: 1,
          lastModified: new Date().toISOString()
        }
      },
      globalComponents: {
        header: [],
        footer: []
      },
      theme: {
        colors: {
          primary: '#000000',
          secondary: '#ffffff',
          accent: '#f0f0f0',
          background: '#000000',
          surface: '#111111',
          text: '#ffffff',
          textSecondary: '#cccccc',
          border: '#333333',
          success: '#10b981',
          warning: '#f59e0b',
          error: '#ef4444'
        },
        typography: {
          fontFamilies: ['Montserrat', 'Roboto', 'Open Sans', 'Lato'],
          headings: {
            ...createDefaultTypography(),
            fontFamily: 'Montserrat',
            fontWeight: 700
          },
          body: {
            ...createDefaultTypography(),
            fontFamily: 'Roboto'
          },
          buttons: {
            ...createDefaultTypography(),
            fontFamily: 'Roboto',
            fontWeight: 600
          },
          navigation: {
            ...createDefaultTypography(),
            fontFamily: 'Roboto',
            fontWeight: 500
          }
        },
        spacing: {
          unit: 4,
          scale: [0, 4, 8, 12, 16, 20, 24, 32, 40, 48, 64, 80, 96, 128]
        },
        breakpoints: {
          mobile: 768,
          tablet: 1024,
          desktop: 1280
        }
      },
      assets: {
        images: {},
        videos: {},
        documents: {}
      },
      settings: {
        siteName: 'Reesha | Growth & Performance Marketing Agency',
        tagline: 'We Grow with Data, Create with Results',
        logo: {
          src: '/images/LOGO-A9.png',
          alt: 'Reesha',
          width: createResponsiveValue(120),
          height: createResponsiveValue(40),
          objectFit: 'contain',
          objectPosition: 'center',
          borderRadius: createResponsiveValue(0),
          overlay: {
            enabled: false,
            color: '#000000',
            opacity: 0
          }
        },
        favicon: '/images/LOGO-A9.png',
        googleAnalytics: '',
        googleTagManager: '',
        customCode: {
          head: '',
          bodyStart: '',
          bodyEnd: ''
        }
      }
    }
  },
  sharedAssets: {
    images: {},
    videos: {},
    documents: {}
  },
  globalSettings: {
    domain: 'reesha.agency',
    timezone: 'Asia/Riyadh',
    dateFormat: 'DD/MM/YYYY',
    currency: 'SAR',
    maintenance: {
      enabled: false,
      message: {
        ar: 'الموقع تحت الصيانة',
        en: 'Site under maintenance'
      }
    }
  },
  version: '2.0.0',
  lastModified: new Date().toISOString()
});

// Advanced multi-language content hook
export const useMultiLanguageWebsite = () => {
  const [website, setWebsite] = useState<MultiLanguageWebsite>(createDefaultMultiLanguageWebsite());
  const [currentLanguage, setCurrentLanguage] = useState<'ar' | 'en'>('ar');
  const [isLoading, setIsLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [devicePreview, setDevicePreview] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [history, setHistory] = useState<MultiLanguageWebsite[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Load data from localStorage or Supabase
  useEffect(() => {
    const loadData = async () => {
      try {
        const savedData = localStorage.getItem('multiLanguageWebsite');
        const savedLanguage = localStorage.getItem('currentLanguage') as 'ar' | 'en';
        
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          setWebsite(parsedData);
        }
        
        if (savedLanguage && (savedLanguage === 'ar' || savedLanguage === 'en')) {
          setCurrentLanguage(savedLanguage);
        }
        
        // TODO: Load from Supabase
        // const { data } = await supabase.from('multilanguage_websites').select('*').single();
        // if (data) setWebsite(data.content);
        
      } catch (error) {
        console.error('Error loading website data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Save data with history tracking
  const saveWebsite = async (newWebsite: MultiLanguageWebsite, addToHistory = true) => {
    if (addToHistory) {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(website);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }

    const updatedWebsite = {
      ...newWebsite,
      lastModified: new Date().toISOString()
    };

    setWebsite(updatedWebsite);
    localStorage.setItem('multiLanguageWebsite', JSON.stringify(updatedWebsite));
    
    // TODO: Save to Supabase
    // await supabase.from('multilanguage_websites').upsert({
    //   id: 'main',
    //   content: updatedWebsite,
    //   updated_at: new Date().toISOString()
    // });
  };

  // Language switching
  const switchLanguage = (lang: 'ar' | 'en') => {
    setCurrentLanguage(lang);
    localStorage.setItem('currentLanguage', lang);
    
    // Update document direction and language
    document.documentElement.dir = website.languages[lang].direction;
    document.documentElement.lang = lang;
  };

  // Get current language website
  const getCurrentWebsite = () => website.websites[currentLanguage];
  
  // Get current language config
  const getCurrentLanguageConfig = () => website.languages[currentLanguage];

  // Page management
  const updatePage = (pageId: string, updates: Partial<LanguagePage>) => {
    const updatedWebsite = {
      ...website,
      websites: {
        ...website.websites,
        [currentLanguage]: {
          ...website.websites[currentLanguage],
          pages: {
            ...website.websites[currentLanguage].pages,
            [pageId]: {
              ...website.websites[currentLanguage].pages[pageId],
              ...updates,
              lastModified: new Date().toISOString(),
              version: website.websites[currentLanguage].pages[pageId].version + 1
            }
          }
        }
      }
    };
    
    saveWebsite(updatedWebsite);
  };

  // Section management
  const updateSection = (pageId: string, sectionId: string, updates: Partial<Section>) => {
    const page = website.websites[currentLanguage].pages[pageId];
    const updatedSections = page.sections.map(section =>
      section.id === sectionId 
        ? { ...section, ...updates, version: section.version + 1 }
        : section
    );
    
    updatePage(pageId, { sections: updatedSections });
  };

  // Widget management
  const updateWidget = (pageId: string, sectionId: string, columnId: string, widgetId: string, updates: Partial<Widget>) => {
    const page = website.websites[currentLanguage].pages[pageId];
    const updatedSections = page.sections.map(section => {
      if (section.id === sectionId) {
        const updatedColumns = section.columns.map(column => {
          if (column.id === columnId) {
            const updatedWidgets = column.widgets.map(widget =>
              widget.id === widgetId 
                ? { ...widget, ...updates, version: widget.version + 1 }
                : widget
            );
            return { ...column, widgets: updatedWidgets };
          }
          return column;
        });
        return { ...section, columns: updatedColumns };
      }
      return section;
    });
    
    updatePage(pageId, { sections: updatedSections });
  };

  // Theme management
  const updateTheme = (updates: Partial<LanguageWebsite['theme']>) => {
    const updatedWebsite = {
      ...website,
      websites: {
        ...website.websites,
        [currentLanguage]: {
          ...website.websites[currentLanguage],
          theme: {
            ...website.websites[currentLanguage].theme,
            ...updates
          }
        }
      }
    };
    
    saveWebsite(updatedWebsite);
  };

  // Asset management
  const addAsset = (type: 'images' | 'videos' | 'documents', asset: any) => {
    const updatedWebsite = {
      ...website,
      websites: {
        ...website.websites,
        [currentLanguage]: {
          ...website.websites[currentLanguage],
          assets: {
            ...website.websites[currentLanguage].assets,
            [type]: {
              ...website.websites[currentLanguage].assets[type],
              [asset.id]: asset
            }
          }
        }
      }
    };
    
    saveWebsite(updatedWebsite);
  };

  // History management
  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setWebsite(history[historyIndex - 1]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setWebsite(history[historyIndex + 1]);
    }
  };

  // Utility functions
  const getResponsiveValue = <T>(value: ResponsiveValue<T>): T => {
    return value[devicePreview];
  };

  const generateUniqueId = (prefix: string = 'element') => {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  return {
    // State
    website,
    currentLanguage,
    isLoading,
    editMode,
    selectedElement,
    devicePreview,
    history,
    historyIndex,

    // Actions
    saveWebsite,
    switchLanguage,
    setEditMode,
    setSelectedElement,
    setDevicePreview,

    // Data getters
    getCurrentWebsite,
    getCurrentLanguageConfig,
    getResponsiveValue,

    // CRUD operations
    updatePage,
    updateSection,
    updateWidget,
    updateTheme,
    addAsset,

    // History
    undo,
    redo,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,

    // Utilities
    generateUniqueId,

    // Getters
    getPage: (pageId: string) => website.websites[currentLanguage].pages[pageId],
    getAllPages: () => Object.values(website.websites[currentLanguage].pages),
    getTheme: () => website.websites[currentLanguage].theme,
    getAssets: () => website.websites[currentLanguage].assets,
    getSettings: () => website.websites[currentLanguage].settings
  };
};